import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { HealthProvider } from './context/HealthContext';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { InstallBanner } from './components/pwa/InstallBanner';
import { UpdateBanner } from './components/pwa/UpdateBanner';
import { skipWaiting } from './swRegistration';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingLitePage from './pages/OnboardingLitePage';
import HomePage from './pages/HomePage';
import ChatPage from './pages/ChatPage';
import CheckInPage from './pages/CheckInPage';
import HealthPage from './pages/HealthPage';
import InsightsPage from './pages/InsightsPage';
import LearnPage from './pages/LearnPage';
import PreparePage from './pages/PreparePage';
import ContactsPage from './pages/ContactsPage';
import ProfilePage from './pages/ProfilePage';
import AppointmentsPage from './pages/AppointmentsPage';
import AppointmentDetailPage from './pages/AppointmentDetailPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentDetailPage from './pages/DocumentDetailPage';
import AboutPage from './pages/AboutPage';
import InvestorPage from './pages/InvestorPage';
import PresentationPage from './pages/PresentationPage';
import TechnicalBriefPage from './pages/TechnicalBriefPage';
import StudioPage from './pages/StudioPage';
import BriefingPage from './pages/BriefingPage';
import ExplainPage from './pages/ExplainPage';
import DemoPage from './pages/DemoPage';
// Stage 20B+ Wave A — public trust surfaces.
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SecurityPage from './pages/SecurityPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
// Wave G-B — medical disclaimer trust page (mandatory for health products).
import MedicalDisclaimerPage from './pages/MedicalDisclaimerPage';
// Stage 20B+ Wave B — viewable sample appointment brief (proof asset).
import SampleBriefPage from './pages/SampleBriefPage';
// Stage 20B+ Wave F — founder-only pilot dashboard.
import FounderPilotPage from './pages/FounderPilotPage';
import FounderPage from './pages/FounderPage';
import FounderIntelligencePage from './pages/FounderIntelligencePage';
import FounderGrowthPage from './pages/FounderGrowthPage';
import FounderMissionControl from './pages/FounderMissionControl';
import ActivationHandoffPage from './pages/ActivationHandoffPage';
import WelcomePage from './pages/WelcomePage';
import AdminInvitesPage from './pages/AdminInvitesPage';
import NDAPage from './pages/NDAPage';
import AdminNDARegister from './pages/AdminNDARegister';
import AdminUsersPage from './pages/AdminUsersPage';
import MedicalTeamPage from './pages/MedicalTeamPage';
import SharedSummaryPage from './pages/SharedSummaryPage';
import BillingPage from './pages/BillingPage';
import PricingPage from './pages/PricingPage';
// V14 · Concept directions (preview only — not linked from homepage)
import ConceptsIndex from './pages/concepts/ConceptsIndex';
import ConceptAPage from './pages/concepts/ConceptAPage';
import ConceptBPage from './pages/concepts/ConceptBPage';
import ConceptCPage from './pages/concepts/ConceptCPage';
import ConceptDPage from './pages/concepts/ConceptDPage';
import { PageErrorBoundary } from './components/shared/PageErrorBoundary';
import { markRouteStart, markRouteEnd } from './services/vitals';
import { RageClickWatcher, NavLoopWatcher } from './hooks/useFrictionEvents';
import '@/App.css';

// Tracks route transitions for the route_load telemetry. Must live inside
// BrowserRouter so useLocation() resolves.
function RouteTelemetry() {
  const location = useLocation();
  useEffect(() => {
    markRouteStart(location.pathname);
    // Defer to next paint so duration reflects render cost.
    const rafId = requestAnimationFrame(() => markRouteEnd(location.pathname));
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname]);
  return null;
}

function App() {
  const [swUpdate, setSwUpdate] = useState(null);

  useEffect(() => {
    const handler = (e) => setSwUpdate(() => skipWaiting);
    window.addEventListener('sw-update', handler);
    return () => window.removeEventListener('sw-update', handler);
  }, []);

  // Wave 3 v2 fix sprint (2026-02) — Cross-platform standalone detection.
  // Some older iOS Safari + Chrome iOS builds don't reliably match the
  // `@media (display-mode: standalone)` rule, so we ALSO toggle a class
  // on <html> based on `navigator.standalone` (iOS) or the matchMedia
  // probe. Both pathways increase the safe-area floor to 44px so that
  // back/X/profile/menu buttons clear iOS Dynamic Island, iOS notch,
  // iPhone SE status bar, AND the Android system status bar in PWA mode.
  useEffect(() => {
    const root = document.documentElement;
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches;
    const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent || '');
    if (isStandalone) root.classList.add('is-standalone');
    if (isStandalone && isIOS) root.classList.add('ios-standalone');
    return () => {
      root.classList.remove('is-standalone');
      root.classList.remove('ios-standalone');
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <HealthProvider>
        <RouteTelemetry />
        <RageClickWatcher />
        <NavLoopWatcher />
        <Toaster position="top-center" richColors closeButton duration={3000} />
        <InstallBanner />
        <UpdateBanner onUpdate={swUpdate} />
        <Routes>
          {/* V14 · "/" now serves ConceptDPage. Old V12/V13 LandingPage kept
              accessible at /landing-v13 as a rollback hatch. PREVIEW ONLY —
              production (kindrahealth.app) is untouched until founder confirms. */}
          <Route path="/" element={<ConceptDPage />} />
          <Route path="/landing-v13" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/investor" element={<InvestorPage />} />
          <Route path="/presentation" element={<PresentationPage />} />
          <Route path="/technical" element={<TechnicalBriefPage />} />
          <Route path="/briefing" element={<BriefingPage />} />
          <Route path="/explain" element={<ExplainPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/founder" element={<ProtectedRoute><FounderPage /></ProtectedRoute>} />
          <Route path="/founder/intelligence" element={<ProtectedRoute><FounderIntelligencePage /></ProtectedRoute>} />
          <Route path="/founder/growth" element={<ProtectedRoute><FounderGrowthPage /></ProtectedRoute>} />
          <Route path="/founder/mission-control" element={<ProtectedRoute><FounderMissionControl /></ProtectedRoute>} />
          <Route path="/activation" element={<ProtectedRoute><ActivationHandoffPage /></ProtectedRoute>} />
          <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
          <Route path="/studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
          <Route path="/confidentiality" element={<NDAPage />} />
          <Route path="/shared/:token" element={<SharedSummaryPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* Stage 20B+ Wave A — public trust surfaces. */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
          {/* Stage 20B+ Wave B — sample appointment brief (proof asset). */}
          <Route path="/sample-brief" element={<SampleBriefPage />} />
          {/* V14 · homepage concept directions (preview only) */}
          <Route path="/concepts"  element={<ConceptsIndex />} />
          <Route path="/concept-a" element={<ConceptAPage />} />
          <Route path="/concept-b" element={<ConceptBPage />} />
          <Route path="/concept-c" element={<ConceptCPage />} />
          <Route path="/concept-d" element={<ConceptDPage />} />
          {/* Stage 20B+ Wave F — founder-only pilot dashboard. */}
          <Route path="/founder/pilot" element={<ProtectedRoute><FounderPilotPage /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/onboarding-lite" element={<ProtectedRoute><OnboardingLitePage /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><PageErrorBoundary name="Billing"><BillingPage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/home" element={<ProtectedRoute><PageErrorBoundary name="Home"><HomePage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><PageErrorBoundary name="Chat"><ChatPage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/checkin" element={<ProtectedRoute><PageErrorBoundary name="CheckIn"><CheckInPage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/health" element={<ProtectedRoute><PageErrorBoundary name="Health"><HealthPage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute><PageErrorBoundary name="Insights"><InsightsPage /></PageErrorBoundary></ProtectedRoute>} />
          <Route path="/learn" element={<ProtectedRoute><LearnPage /></ProtectedRoute>} />
          <Route path="/prepare" element={<ProtectedRoute><PreparePage /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
          <Route path="/appointments/:id" element={<ProtectedRoute><AppointmentDetailPage /></ProtectedRoute>} />
          <Route path="/appointments/:id/prep" element={<ProtectedRoute><AppointmentDetailPage initialTab="prep" /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
          <Route path="/documents/:id" element={<ProtectedRoute><DocumentDetailPage /></ProtectedRoute>} />
          <Route path="/admin/invites" element={<ProtectedRoute><AdminInvitesPage /></ProtectedRoute>} />
          <Route path="/admin/agreements" element={<ProtectedRoute><AdminNDARegister /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/medical-team" element={<ProtectedRoute><MedicalTeamPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </HealthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
